import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface DataPoint {
  x: number;
  y: number;
  z: number;
  value: number;
  label: string;
  color: string;
}

interface DataVisualization3DProps {
  data: DataPoint[];
  height?: number;
  width?: number;
  depth?: number;
  animate?: boolean;
}

export function DataVisualization3D({ 
  data, 
  height = 5, 
  width = 10, 
  depth = 5,
  animate = true 
}: DataVisualization3DProps) {
  const groupRef = useRef<THREE.Group>(null);

  const bars = useMemo(() => {
    return data.map((point, index) => {
      const barHeight = (point.value / Math.max(...data.map(d => d.value))) * height;
      const x = (point.x / Math.max(...data.map(d => d.x))) * width - width / 2;
      const z = (point.z / Math.max(...data.map(d => d.z))) * depth - depth / 2;
      
      return {
        ...point,
        barHeight,
        position: [x, barHeight / 2, z] as [number, number, number],
        scale: [0.3, barHeight, 0.3] as [number, number, number]
      };
    });
  }, [data, height, width, depth]);

  useFrame((state) => {
    if (groupRef.current && animate) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.1;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Grid lines */}
      <gridHelper args={[width, 10, 0x444444, 0x222222]} />
      
      {/* Data bars */}
      {bars.map((bar, index) => (
        <group key={index} position={bar.position}>
          <mesh>
            <boxGeometry args={[0.3, bar.barHeight, 0.3]} />
            <meshStandardMaterial 
              color={bar.color} 
              transparent 
              opacity={0.8}
              metalness={0.2}
              roughness={0.3}
            />
          </mesh>
        </group>
      ))}
      
      {/* Axes */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, height, 8]} />
        <meshStandardMaterial color="#ff0000" />
      </mesh>
      
      <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.05, 0.05, width, 8]} />
        <meshStandardMaterial color="#00ff00" />
      </mesh>
      
      <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.05, 0.05, depth, 8]} />
        <meshStandardMaterial color="#0000ff" />
      </mesh>
    </group>
  );
}

// Performance metrics visualization
interface PerformanceMetrics3DProps {
  agents: Array<{
    id: string;
    name: string;
    performance: number;
    tasksCompleted: number;
    efficiency: number;
  }>;
}

export function PerformanceMetrics3D({ agents }: PerformanceMetrics3DProps) {
  const data: DataPoint[] = agents.map((agent, index) => ({
    x: index,
    y: agent.performance,
    z: agent.efficiency,
    value: agent.tasksCompleted,
    label: agent.name,
    color: `hsl(${index * 60}, 70%, 60%)`
  }));

  return (
    <DataVisualization3D 
      data={data} 
      height={6} 
      width={12} 
      depth={6}
      animate={true}
    />
  );
}
